import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { labTestAPI, labTestOrderAPI } from '../services/api';
import { formatApiError, formatMoney, formatDateTime, statusBadgeClass } from '../utils/labTestUtils';
import './LabTestOrderDetailPage.css';

import Icon from '../components/Icon';
import OverflowMenu from '../components/OverflowMenu';
// "SAMPLE_COLLECTED" -> "Sample Collected". Only used for order.previuStatus
// (our own OrderStatus enum) — Thyrocare's own tracking steps already carry
// their own statusText/label from the partner API, formatted upstream.
function formatStatusLabel(status) {
  return String(status || '')
    .toLowerCase()
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const initialsOf = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '—';

// "18:30" -> { clock: '6:30', ampm: 'PM' }. Anything unparseable comes back
// whole with no suffix, so an odd partner format still shows something.
function splitClock(t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  if (!m) return { clock: t || '—', ampm: '' };
  const h = Number(m[1]);
  return { clock: `${h % 12 || 12}:${m[2]}`, ampm: h >= 12 ? 'PM' : 'AM' };
}

const TESTS_COLLAPSED = 8;

export default function LabTestOrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showAllTests, setShowAllTests] = useState(false);

  // 28 Jul 2026: new — Cancel Order (Group B: Thyrocare cancel + auto wallet refund)
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  // 28 Jul 2026: new — replaces window.prompt() with a properly styled
  // in-app modal, matching the rest of this page instead of the browser's
  // native (and jarring) prompt dialog.
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReasonInput, setCancelReasonInput] = useState('');

  // Pull report — manually triggers the Thyrocare report download instead of
  // waiting for a partner webhook to mark the order report-ready.
  const [pullingReport, setPullingReport] = useState(false);
  const [pullReportError, setPullReportError] = useState('');
  const [pullReportSuccess, setPullReportSuccess] = useState('');

  // 28 Jul 2026: new — Request Refund (Group C: manual navigator refund form)
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [refundSuccess, setRefundSuccess] = useState('');

  const fetchOrder = useCallback(async (silent = false) => {
    if (!orderId) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError('');
    try {
      const res = await labTestAPI.getOrder(orderId);
      setOrder(res.data || null);
    } catch (err) {
      setError(formatApiError(err.response?.data) || err.message || 'Failed to load order details.');
      setOrder(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder(false);
  }, [orderId, fetchOrder]);

  // 28 Jul 2026: new — Group B. orderId here is already the Thyrocare
  // partner order id (this page's whole data model is keyed on it — see
  // labTestAPI.getOrder(orderId) above), which is exactly what the new
  // cancel endpoint expects, so no extra lookup is needed.
  const handleCancelOrder = () => {
    // 28 Jul 2026 — simplified: window.confirm()'s own native dialog is
    // gone too — the modal below already asks for confirmation as part of
    // its own Cancel/Confirm buttons, so a second native confirm on top of
    // it was redundant.
    setCancelReasonInput('');
    setCancelError('');
    setShowCancelModal(true);
  };

  // 28 Jul 2026 — new. Thyrocare's own cancellation API requires a reason
  // in the request body (confirmed via their Postman collection) — asking
  // here gives a real audit trail instead of always sending a generic
  // placeholder. Leaving it blank still works (backend falls back to a
  // default), so this doesn't block a navigator in a hurry.
  const confirmCancelOrder = async () => {
    setShowCancelModal(false);
    setCancelling(true);
    setCancelError('');
    try {
      await labTestAPI.cancelOrder(orderId, cancelReasonInput.trim() || undefined);
      await fetchOrder(true);
    } catch (err) {
      setCancelError(
        formatApiError(err.response?.data) || err.message || 'Failed to cancel order.'
      );
    } finally {
      setCancelling(false);
    }
  };

  const handlePullReport = async () => {
    setPullingReport(true);
    setPullReportError('');
    setPullReportSuccess('');
    try {
      const res = await labTestOrderAPI.pullReport(orderId);
      setPullReportSuccess(
        `Report pulled and saved to the patient's health records (record ${res.data?.id || ''}).`
      );
    } catch (err) {
      setPullReportError(
        formatApiError(err.response?.data) || err.message || 'Failed to pull the report.'
      );
    } finally {
      setPullingReport(false);
    }
  };

  // 28 Jul 2026: new — Group C. Manual navigator refund request — the
  // navigator decides the amount (unlike the automatic Group B refund),
  // since a cancellation here doesn't necessarily mean the whole order:
  // partial refunds (e.g. collection already happened) are a real case
  // this form needs to support, not just "refund everything." Sends only
  // `amount` — backend's requestRefund() always credits it to the wallet
  // now (Deepu, 28 Jul 2026: all refunds go to wallet, no more separate
  // Razorpay-refund path), so the earlier walletRefundAmountMinorUnits
  // field is gone — one number, one meaning.
  const handleRequestRefund = async (e) => {
    e.preventDefault();
    setRefundError('');
    setRefundSuccess('');

    const rupees = Number(refundAmount);
    if (!rupees || rupees <= 0) {
      setRefundError('Enter a valid refund amount.');
      return;
    }

    setRefundSubmitting(true);
    try {
      const res = await labTestOrderAPI.refundRequest({
        patientId: patient?.attributes?.externalPatientId,
        amount: rupees,
        currency: 'INR',
        planId: planItem?.id,
        // DEF-005: prefer the server-resolved Previu branded name over the
        // partner's own product name. See LabTestOrderDTO.planDisplayName.
        planName: order?.planDisplayName || planItem?.name,
        noOfTests: tests.length || undefined,
        patientName: patient?.name,
        patientPhone: patient?.contactNumber || order?.contactNumber,
        patientEmail: order?.email,
        reason: refundReason || 'Refund requested by navigator',
      });
      setRefundSuccess(res.data?.message || 'Refund submitted successfully.');
      setRefundAmount('');
      setRefundReason('');
      setShowRefundForm(false);
    } catch (err) {
      setRefundError(
        formatApiError(err.response?.data) || err.message || 'Failed to submit refund request.'
      );
    } finally {
      setRefundSubmitting(false);
    }
  };

  const breadcrumbs = [
    { label: 'Lab Tests', to: '/lab-tests' },
    { label: orderId || 'Order' },
  ];

  const tracking = order?.orderTracking || [];
  const patient = order?.patients?.[0];
  const planItem = patient?.items?.[0];
  const tests = planItem?.tests || [];
  const appt = order?.appointmentDetails;
  const price = order?.price;
  const payment = order?.paymentDetails;
  const isCancelledOrder = String(order?.orderStatus || '').toLowerCase().includes('cancel');
  const startClock = splitClock(appt?.startTimeIn24HrFormat);
  const addressText = order?.address?.addressLine1
    || [order?.address?.street, order?.address?.city].filter(Boolean).join(', ')
    || patient?.address
    || '—';

  return (
    <AppShell breadcrumbs={breadcrumbs}>
      {/* 28 Jul 2026: new — replaces window.prompt() with a properly styled
          in-app modal. Rendered at the top of the tree as a fixed overlay,
          same pattern as any other confirmation dialog in this app. */}
      {showCancelModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(13,30,53,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowCancelModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-surface)', borderRadius: 12, padding: '24px 28px',
              width: '90%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: 'var(--text-primary)' }}>
              Cancel this order?
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
              This cancels it with Thyrocare and automatically refunds any wallet
              portion of what the patient paid. This can't be undone.
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              Reason for cancellation <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional, sent to Thyrocare)</span>
            </label>
            <input
              type="text"
              autoFocus
              value={cancelReasonInput}
              onChange={(e) => setCancelReasonInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmCancelOrder(); }}
              placeholder="e.g. Patient has an emergency"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                border: '1px solid var(--border)', fontSize: 14, marginBottom: 20,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)',
                  background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Keep order
              </button>
              <button
                type="button"
                onClick={confirmCancelOrder}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: 'none',
                  background: 'var(--red)', color: 'var(--text-on-brand-dark)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel order
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="lab-order-detail">
        <span className="back-link fade-in-up" onClick={() => navigate('/lab-tests')}>
          ← All lab tests
        </span>

        {loading ? (
          <div className="loading-state fade-in-up" style={{ padding: '48px 0' }}>
            <div className="spinner" />
            Loading order {orderId}…
          </div>
        ) : error ? (
          <div className="fade-in-up">
            <div className="alert alert-error" style={{ marginBottom: 16 }}><Icon name="alert" size={14} /> {error}</div>
            <button type="button" className="btn btn-secondary" onClick={() => fetchOrder(false)}>
              Retry
            </button>
          </div>
        ) : order ? (
          <>
            <div className="order-head fade-in-up">
              <div>
                <div className="order-head__eyebrow">Order {order.orderId}</div>
                <h1 className="order-head__title">{order.planDisplayName || planItem?.name || order.orderId}</h1>
                <div className="order-head__meta">
                  <span className={`badge badge--dot ${statusBadgeClass(order.orderStatus)}`}>
                    {order.statusText || order.orderStatus || '—'}
                  </span>
                  {order.orderType && <span>{order.orderType} collection</span>}
                  <span>Placed {formatDateTime(order.orderDate) || '—'}</span>
                  {order.refOrderNo && <span>Ref <span className="mono">{order.refOrderNo}</span></span>}
                </div>
              </div>
              <div className="order-head__actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-with-icon"
                  disabled={refreshing}
                  onClick={() => fetchOrder(true)}
                >
                  <Icon name="refresh" size={16} /> {refreshing ? 'Refreshing…' : 'Refresh'}
                </button>
                {/* Pull report — for cases where the partner webhook hasn't (yet)
                    marked the order report-ready but the report already exists
                    at Thyrocare. Backend call is not idempotent — each click
                    writes a new health record if it succeeds — so this is left
                    as a plain button rather than something auto-retried. */}
                <button
                  type="button"
                  className="btn btn-primary btn-with-icon"
                  disabled={pullingReport}
                  onClick={handlePullReport}
                >
                  <Icon name="download" size={16} /> {pullingReport ? 'Pulling report…' : 'Pull report'}
                </button>
                {/* 28 Jul 2026: Group B cancel (hidden once cancelled; the
                    backend is idempotent regardless) and Group C manual
                    refund — see handleRequestRefund for why the amount stays
                    navigator-entered. They moved into this menu with the
                    redesign. */}
                <OverflowMenu
                  label="More order actions"
                  items={[
                    patient?.attributes?.externalPatientId && {
                      label: 'View patient profile',
                      onSelect: () => navigate(`/patients/${patient.attributes.externalPatientId}`),
                    },
                    { label: showRefundForm ? 'Hide refund form' : 'Request refund', onSelect: () => setShowRefundForm(v => !v) },
                    !isCancelledOrder && {
                      label: cancelling ? 'Cancelling…' : 'Cancel order',
                      danger: true,
                      disabled: cancelling,
                      onSelect: handleCancelOrder,
                    },
                  ].filter(Boolean)}
                />
              </div>
            </div>

            {cancelError && (
              <div className="alert alert-error fade-in-up" style={{ marginBottom: 16 }}>
                <Icon name="alert" size={14} /> {cancelError}
              </div>
            )}

            {pullReportError && (
              <div className="alert alert-error fade-in-up" style={{ marginBottom: 16 }}>
                <Icon name="alert" size={14} /> {pullReportError}
              </div>
            )}
            {pullReportSuccess && (
              <div className="alert alert-success fade-in-up" style={{ marginBottom: 16 }}>
                <Icon name="check" size={14} /> {pullReportSuccess}
              </div>
            )}

            {showRefundForm && (
              <form onSubmit={handleRequestRefund} className="lab-order-card fade-in-up" style={{ marginBottom: 16 }}>
                <div className="lab-order-card-title">Request refund</div>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0, marginBottom: 14 }}>
                  Credits the entered amount straight to the patient's Previu wallet. Use this
                  for partial refunds or orders paid via the wallet — "Cancel order" in the
                  menu above gives a full refund that also cancels with Thyrocare.
                </p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                  <div style={{ flex: '1 1 160px' }}>
                    <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                      Refund amount (₹)
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="form-input"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      placeholder={price?.netPayableAmount ? String(price.netPayableAmount) : 'e.g. 3999'}
                      required
                    />
                  </div>
                  <div style={{ flex: '2 1 240px' }}>
                    <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                      Reason
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="e.g. Patient requested cancellation before collection"
                    />
                  </div>
                </div>
                {refundError && (
                  <div className="alert alert-error" style={{ marginBottom: 12 }}><Icon name="alert" size={14} /> {refundError}</div>
                )}
                {refundSuccess && (
                  <div className="alert alert-success" style={{ marginBottom: 12 }}><Icon name="check" size={14} /> {refundSuccess}</div>
                )}
                <button type="submit" className="btn btn-primary btn-sm" disabled={refundSubmitting}>
                  {refundSubmitting ? 'Submitting…' : 'Submit refund'}
                </button>
              </form>
            )}

            <div className="lab-order-detail-grid fade-in-up delay-1">
              <div className="lab-order-detail-main">
                {tracking.length > 0 && (() => {
                  // 28 Jul 2026 — Thyrocare's tracking array has no concept of
                  // "cancelled": is_current_status/is_completed reflect THEIR
                  // pipeline only, so a cancelled order would show its last
                  // step as current forever. Insert a CANCELLED node right
                  // after the last step actually reached, so the stepper
                  // reads as one story ("reached ASSIGNED, then cancelled").
                  let lastReachedIndex = -1;
                  if (isCancelledOrder) {
                    tracking.forEach((step, i) => {
                      const attrs = step.attributes || {};
                      if (attrs.is_completed === true || attrs.is_current_status === true) {
                        lastReachedIndex = i;
                      }
                    });
                  }
                  const steps = tracking.map((step, i) => {
                    const attrs = step.attributes || {};
                    const isCurrent = !isCancelledOrder && attrs.is_current_status === true;
                    const isDone = isCancelledOrder ? i <= lastReachedIndex : attrs.is_completed === true;
                    return { key: `${step.status}-${i}`, step, state: isCurrent ? 'current' : isDone ? 'done' : 'pending' };
                  });
                  if (isCancelledOrder) {
                    steps.splice(lastReachedIndex + 1, 0, { key: 'cancelled', cancelled: true, state: 'cancelled' });
                  }

                  return (
                  <div className="lab-order-card">
                    <div className="lab-order-card-head">
                      <div>
                        <div className="lab-order-card-heading">Order progress</div>
                        {(order.statusDescription || order.statusComments) && (
                          <div className="lab-order-card-sub">{order.statusDescription || order.statusComments}</div>
                        )}
                      </div>
                      {/* previuStatus is OUR OWN internal status — independent of
                          the steps below, which are Thyrocare's own tracking, proxied
                          verbatim. The two can legitimately disagree: we mark COMPLETED
                          once we've successfully imported a report, even if Thyrocare's
                          own tracking hasn't reached LAB/FULL REPORTED yet. */}
                      {order.previuStatus && (
                        <span
                          className={`badge badge--dot ${statusBadgeClass(order.previuStatus)}`}
                          title="Previu's own order status — independent of Thyrocare's tracking steps below. Set once we've successfully imported the report, even if Thyrocare's own tracking hasn't caught up yet."
                        >
                          Previu: {formatStatusLabel(order.previuStatus)}
                        </span>
                      )}
                    </div>
                    <ol className="h-stepper">
                      {steps.map(({ key, step, state, cancelled }, n) => (
                        <li
                          key={key}
                          className={`h-stepper__step is-${state}`}
                          aria-current={state === 'current' ? 'step' : undefined}
                        >
                          <span className="h-stepper__node" aria-hidden="true">
                            {state === 'done' ? <Icon name="check" size={18} /> : cancelled ? '✕' : n + 1}
                          </span>
                          <div className="h-stepper__title">
                            {cancelled ? 'Cancelled' : (step.statusText || step.status)}
                          </div>
                          {cancelled ? (
                            <div className="h-stepper__desc">
                              Cancelled here — the steps before this reflect Thyrocare's own progress.
                            </div>
                          ) : (
                            <>
                              {step.statusDescription && <div className="h-stepper__desc">{step.statusDescription}</div>}
                              {step.comments && <div className="h-stepper__desc">{step.comments}</div>}
                              {step.timestamp && <div className="h-stepper__time">{formatDateTime(step.timestamp)}</div>}
                            </>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                  );
                })()}

                {patient && (
                  <div className="lab-order-card">
                    <div className="lab-order-card-head">
                      <div>
                        <div className="lab-order-card-heading">Patient &amp; tests</div>
                        <div className="lab-order-card-sub">Collection details for this order</div>
                      </div>
                      {patient.attributes?.externalPatientId && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm btn-with-icon"
                          onClick={() => navigate(`/patients/${patient.attributes.externalPatientId}`)}
                        >
                          View patient <Icon name="arrowRight" size={14} />
                        </button>
                      )}
                    </div>
                    <div className="order-patient">
                      <span className="avatar" aria-hidden="true">{initialsOf(patient.name)}</span>
                      <div>
                        <div className="order-patient__name">{patient.name}</div>
                        <div className="order-patient__sub">
                          {[patient.gender, patient.age && `${patient.age} ${patient.ageType || ''}`.trim(), patient.contactNumber]
                            .filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      {patient.attributes?.isFastingRequired && (
                        <span className="badge badge--dot badge-pending order-patient__tag">Fasting required</span>
                      )}
                    </div>
                    {planItem && (
                      <div className="order-facts">
                        <div>
                          <div className="order-facts__label">Package</div>
                          <div className="order-facts__value">{order.planDisplayName || planItem.name}</div>
                        </div>
                        <div>
                          <div className="order-facts__label">Tests included</div>
                          <div className="order-facts__value">{tests.length ? `${tests.length} biomarkers` : '—'}</div>
                        </div>
                        <div>
                          <div className="order-facts__label">Type</div>
                          <div className="order-facts__value">{planItem.type || '—'}</div>
                        </div>
                      </div>
                    )}
                    {tests.length > 0 && (
                      <ul className="test-chips">
                        {(showAllTests ? tests : tests.slice(0, TESTS_COLLAPSED)).map((t) => (
                          <li key={t.testId} className="test-chip">
                            <Icon name="check" size={14} /> {t.testId}
                          </li>
                        ))}
                        {tests.length > TESTS_COLLAPSED && (
                          <li>
                            <button type="button" className="test-chip" onClick={() => setShowAllTests(v => !v)}>
                              {showAllTests ? 'Show fewer' : `+ ${tests.length - TESTS_COLLAPSED} more`}
                            </button>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div className="lab-order-detail-aside">
                <div className="lab-order-card">
                  <div className="appt-card__when">
                    <span className="appt-card__icon"><Icon name="calendar" size={26} /></span>
                    <div>
                      <div className="appt-card__eyebrow">Appointment</div>
                      <div className="appt-card__date">
                        {appt?.date || formatDateTime(order.appointmentDate)?.split(',')[0] || '—'}
                      </div>
                    </div>
                  </div>
                  <div className="appt-card__time">
                    <span className="appt-card__clock">{startClock.clock}</span>
                    {startClock.ampm && <span className="appt-card__ampm">{startClock.ampm}</span>}
                    {appt?.slotDurationInMin && (
                      <span className="appt-card__window">{appt.slotDurationInMin} min window</span>
                    )}
                  </div>
                  <div className="appt-card__addr">
                    <Icon name="home" size={20} className="icon" />
                    <div>
                      {addressText}
                      {order.address?.pincode && <> · PIN {order.address.pincode}</>}
                    </div>
                  </div>
                </div>

                {(price || payment) && (
                  <div className="lab-order-card">
                    <div className="lab-order-card-heading" style={{ marginBottom: 14 }}>Payment</div>
                    <dl className="lab-order-kv">
                      {payment?.payType && (
                        <div className="lab-order-kv-row">
                          <dt>Payment type</dt>
                          <dd>{payment.payType}</dd>
                        </div>
                      )}
                      {price?.totalMrp && (
                        <div className="lab-order-kv-row">
                          <dt>MRP</dt>
                          <dd>{formatMoney(price.totalMrp)}</dd>
                        </div>
                      )}
                      {price?.netPayableAmount != null && (
                        <div className="lab-order-kv-row">
                          <dt>Order total</dt>
                          <dd>{formatMoney(price.netPayableAmount || price.totalSellingPrice)}</dd>
                        </div>
                      )}
                      {payment?.paidAmount != null && (
                        <div className="lab-order-kv-row">
                          <dt>Paid</dt>
                          <dd>{formatMoney(payment.paidAmount)}</dd>
                        </div>
                      )}
                    </dl>
                    {payment?.unpaidAmount != null && (
                      <div className="pay-due">
                        <span className="pay-due__label">Amount due</span>
                        <span className="pay-due__amount">{formatMoney(payment.unpaidAmount)}</span>
                      </div>
                    )}
                  </div>
                )}

                {order.otp && (
                  <div className="lab-order-card">
                    <div className="order-facts__label">Collection OTP</div>
                    <div className="order-otp-value">{order.otp}</div>
                  </div>
                )}

                <div className="lab-order-card">
                  <div className="lab-order-card-heading" style={{ marginBottom: 14 }}>Contact</div>
                  <dl className="lab-order-kv">
                    <div className="lab-order-kv-row">
                      <dt>Phone</dt>
                      <dd>{order.contactNumber || '—'}</dd>
                    </div>
                    <div className="lab-order-kv-row">
                      <dt>Email</dt>
                      <dd style={{ fontSize: 12 }}>{order.email || '—'}</dd>
                    </div>
                  </dl>
                </div>

                {order.phlebo?.name && (
                  <div className="lab-order-card">
                    <div className="lab-order-card-heading" style={{ marginBottom: 14 }}>Phlebotomist</div>
                    <p style={{ margin: 0, fontWeight: 600 }}>{order.phlebo.name}</p>
                    {order.phlebo.contactNumber && (
                      <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                        {order.phlebo.contactNumber}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AppShell>
  );
}
